import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import DownloadIcon from '@mui/icons-material/Download'
import PrintIcon from '@mui/icons-material/Print'
import ScienceIcon from '@mui/icons-material/Science'
import type { GridColDef } from '@mui/x-data-grid'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import DateTextField from '@/components/DateTextField'
import PageHeader from '@/components/PageHeader'
import { usePermission } from '@/hooks/usePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useGetFactoryBaoLocContextQuery,
  useGetFactoryBaoLocTabRowsQuery,
  useSaveFactoryBaoLocDateRangeMutation,
  type FactoryBaoLocCell,
  type FactoryBaoLocDateRange,
} from '@/store/api/factoryBaoLocApi'
import { downloadFactoryBaoLocExport } from './baoLoc/export'
import {
  DEFAULT_FACTORY_BL_TAB,
  FACTORY_BL_TABS,
  isFactoryBLTabKey,
  type FactoryBLAction,
  type FactoryBLColumnConfig,
  type FactoryBLTabConfig,
} from './baoLoc/tabs'

type FactoryBLRow = { id: string | number } & Record<string, FactoryBaoLocCell>

interface PendingAction {
  action: FactoryBLAction
  row: FactoryBLRow | null
  tab: FactoryBLTabConfig
}

const FILTER_STORAGE_KEY = 'tlc:factory:bao-loc:filters'

const headerButtonSx = {
  minHeight: 26,
  px: 0.75,
  py: 0.2,
  fontSize: '0.68rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
}

const headerLabelSx = {
  whiteSpace: 'nowrap',
  fontSize: '0.72rem',
  lineHeight: 1.1,
}

const compactInputSx = {
  width: { xs: 124, sm: 132 },
  '& .MuiInputBase-root': {
    minHeight: 26,
    fontSize: '0.72rem',
  },
  '& .MuiInputBase-input': {
    py: 0.25,
    px: 0.75,
  },
}

function getDefaultDateRange(): FactoryBaoLocDateRange {
  const now = dayjs()
  const fiscalYearStart = now.month() >= 9 ? dayjs(`${now.year()}-10-01`) : dayjs(`${now.year() - 1}-10-01`)
  const fiscalYearEnd = now.month() >= 9 ? dayjs(`${now.year() + 1}-09-30`) : dayjs(`${now.year()}-09-30`)

  return {
    startDate: fiscalYearStart.format('YYYY-MM-DD'),
    endDate: fiscalYearEnd.format('YYYY-MM-DD'),
  }
}

function readStoredDateRange(): FactoryBaoLocDateRange {
  if (typeof window === 'undefined') return getDefaultDateRange()

  const raw = window.localStorage.getItem(FILTER_STORAGE_KEY)
  if (!raw) return getDefaultDateRange()

  try {
    const parsed = JSON.parse(raw) as Partial<FactoryBaoLocDateRange>
    const defaults = getDefaultDateRange()
    return {
      startDate: typeof parsed.startDate === 'string' && parsed.startDate ? parsed.startDate : defaults.startDate,
      endDate: typeof parsed.endDate === 'string' && parsed.endDate ? parsed.endDate : defaults.endDate,
    }
  } catch {
    return getDefaultDateRange()
  }
}

function normalizeRows(
  apiRows: Array<Record<string, FactoryBaoLocCell>> | undefined,
  sampleRows: FactoryBLTabConfig['sampleRows'],
): FactoryBLRow[] {
  const sourceRows = apiRows && apiRows.length ? apiRows : sampleRows
  return sourceRows.map((row, index) => {
    const rawId = row.id ?? row.stack_id ?? row.grn_id ?? row.gdn_id ?? row.lot_id ?? row.po_no ?? index + 1
    return { id: rawId as string | number, ...row } as FactoryBLRow
  })
}

function buildGridColumns(columns: FactoryBLColumnConfig[]): GridColDef<FactoryBLRow>[] {
  return columns.map((column) => ({
    field: column.field,
    headerName: column.headerName,
    width: column.width,
    flex: column.flex,
    type: column.type === 'number' ? 'number' : 'string',
    align: column.align,
    headerAlign: column.align,
    valueFormatter: column.type === 'number'
      ? (value: unknown) => (typeof value === 'number' ? value.toLocaleString('en-US') : value)
      : undefined,
  }))
}

function getActionLabel(action: FactoryBLAction) {
  const labels: Record<FactoryBLAction, string> = {
    new: 'New',
    edit: 'Edit',
    delete: 'Delete',
    copy: 'Copy',
    quality: 'Quality',
    print: 'Print',
  }
  return labels[action]
}

function getActionIcon(action: FactoryBLAction) {
  const props = { fontSize: 'small' as const }
  if (action === 'new') return <AddIcon {...props} />
  if (action === 'edit') return <EditIcon {...props} />
  if (action === 'delete') return <DeleteIcon {...props} />
  if (action === 'copy') return <ContentCopyIcon {...props} />
  if (action === 'quality') return <ScienceIcon {...props} />
  return <PrintIcon {...props} />
}

function FactoryBLHeader({
  title,
  filters,
  invalidRange,
  exporting,
  onFilterChange,
  onExport,
}: {
  title: string
  filters: FactoryBaoLocDateRange
  invalidRange: boolean
  exporting: boolean
  onFilterChange: (next: FactoryBaoLocDateRange) => void
  onExport: (filtered: boolean) => void
}) {
  return (
    <PageHeader
      title={title}
      module="Factory"
      actions={
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={{ xs: 0.55, lg: 0.75 }}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                color: 'primary.dark',
                fontWeight: 900,
                lineHeight: 1.15,
                textTransform: 'uppercase',
                fontSize: { xs: '0.92rem', sm: '1rem' },
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            <Chip
              size="small"
              label="TLBL"
              sx={{
                height: 22,
                borderRadius: 999,
                bgcolor: 'rgba(244, 223, 156, 0.34)',
                color: 'secondary.dark',
                border: '1px solid rgba(218, 131, 60, 0.28)',
                fontWeight: 900,
                fontSize: '0.65rem',
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={0.45}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
          >
            <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
              Date:
            </Typography>
            <DateTextField
              size="small"
              value={filters.startDate}
              error={invalidRange}
              onChange={(value) => onFilterChange({ ...filters, startDate: value || getDefaultDateRange().startDate })}
              sx={compactInputSx}
              nativeInputProps={{ 'aria-label': 'Factory Bao Loc start date' }}
            />
            <DateTextField
              size="small"
              value={filters.endDate}
              error={invalidRange}
              onChange={(value) => onFilterChange({ ...filters, endDate: value || getDefaultDateRange().endDate })}
              sx={compactInputSx}
              nativeInputProps={{ 'aria-label': 'Factory Bao Loc end date' }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon fontSize="small" />}
              sx={headerButtonSx}
              disabled={invalidRange || exporting}
              onClick={() => onExport(true)}
            >
              Export (Filter)
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon fontSize="small" />}
              sx={headerButtonSx}
              disabled={exporting}
              onClick={() => onExport(false)}
            >
              Export Excel
            </Button>
          </Stack>
        </Stack>
      }
    />
  )
}

function FactoryBLSummary() {
  const metricRows: FactoryBLRow[] = [
    { id: 'opening', metric: 'Opening stock', bag: 640, net_weight: 38400, value: 38400 },
    { id: 'received', metric: 'Received', bag: 300, net_weight: 18000, value: 18000 },
    { id: 'issued', metric: 'Issued to production', bag: 140, net_weight: 8400, value: 8400 },
    { id: 'delivery', metric: 'Ex-factory', bag: 210, net_weight: 12600, value: 12600 },
    { id: 'inventory', metric: 'Inventory', bag: 590, net_weight: 35400, value: 35400 },
    { id: 'consumables', metric: 'Consumables movement', bag: 0, net_weight: 620, value: 620 },
  ]

  const qualityRows: FactoryBLRow[] = [
    { id: 'R1', quality_code: 'R1', bag: 300, net_weight: 18000, percent: 50.8 },
    { id: 'R2', quality_code: 'R2', bag: 220, net_weight: 13200, percent: 37.3 },
    { id: 'Screen 16', quality_code: 'Screen 16', bag: 70, net_weight: 4200, percent: 11.9 },
  ]

  const categoryRows: FactoryBLRow[] = [
    { id: 'green', category: 'Green coffee', bag: 420, net_weight: 25200 },
    { id: 'processed', category: 'Processed', bag: 170, net_weight: 10200 },
  ]

  const summaryColumns = buildGridColumns([
    { field: 'metric', headerName: 'Metric', flex: 1, width: 220 },
    { field: 'bag', headerName: 'Bag', width: 110, align: 'right', type: 'number' },
    { field: 'net_weight', headerName: 'Net Wt', width: 130, align: 'right', type: 'number' },
    { field: 'value', headerName: 'Value', width: 130, align: 'right', type: 'number' },
  ])
  const qualityColumns = buildGridColumns([
    { field: 'quality_code', headerName: 'Quality', flex: 1, width: 150 },
    { field: 'bag', headerName: 'Bag', width: 100, align: 'right', type: 'number' },
    { field: 'net_weight', headerName: 'Net Wt', width: 130, align: 'right', type: 'number' },
    { field: 'percent', headerName: '%', width: 90, align: 'right', type: 'number' },
  ])
  const categoryColumns = buildGridColumns([
    { field: 'category', headerName: 'Category', flex: 1, width: 170 },
    { field: 'bag', headerName: 'Bag', width: 100, align: 'right', type: 'number' },
    { field: 'net_weight', headerName: 'Net Wt', width: 130, align: 'right', type: 'number' },
  ])

  return (
    <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: '1.35fr 1fr' },
          gap: 1.5,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            Summary
          </Typography>
          <DataTableMUI rows={metricRows} columns={summaryColumns as GridColDef[]} height={310} hideFooter />
        </Paper>
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              Stock by Quality
            </Typography>
            <DataTableMUI rows={qualityRows} columns={qualityColumns as GridColDef[]} height={180} hideFooter />
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              Stock by Category
            </Typography>
            <DataTableMUI rows={categoryRows} columns={categoryColumns as GridColDef[]} height={150} hideFooter />
          </Paper>
        </Stack>
      </Box>
    </Stack>
  )
}

function FactoryBLToolbar({
  actions,
  selectedRow,
  canEdit,
  onAction,
}: {
  actions: FactoryBLAction[]
  selectedRow: FactoryBLRow | null
  canEdit: boolean
  onAction: (action: FactoryBLAction) => void
}) {
  if (!actions.length) return null

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
      {actions.map((action) => {
        const requiresRow = action !== 'new'
        const disabled = !canEdit || (requiresRow && !selectedRow)
        const label = getActionLabel(action)
        return (
          <Tooltip key={action} title={requiresRow && !selectedRow ? `${label}: select a row first` : label}>
            <span>
              <Button
                size="small"
                variant={action === 'new' ? 'contained' : 'outlined'}
                color={action === 'delete' ? 'error' : 'primary'}
                startIcon={getActionIcon(action)}
                disabled={disabled}
                sx={headerButtonSx}
                onClick={() => onAction(action)}
              >
                {label}
              </Button>
            </span>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

function FactoryBLListWorkspace({
  tab,
  rows,
  loading,
  selectedRow,
  selectorValue,
  canEdit,
  onSelectorChange,
  onSelectedRowChange,
  onAction,
}: {
  tab: FactoryBLTabConfig
  rows: FactoryBLRow[]
  loading: boolean
  selectedRow: FactoryBLRow | null
  selectorValue?: string
  canEdit: boolean
  onSelectorChange: (value: string) => void
  onSelectedRowChange: (row: FactoryBLRow | null) => void
  onAction: (action: FactoryBLAction) => void
}) {
  const selectedOption = tab.selectors?.find((option) => option.value === selectorValue)
  const visibleActions = selectedOption?.toolbarVisible === false ? [] : tab.actions
  const columns = useMemo(() => buildGridColumns(tab.columns), [tab.columns])

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
          {tab.selectors ? (
            <TextField
              select
              size="small"
              label="Dataset"
              value={selectorValue ?? tab.selectors[0]?.value ?? ''}
              onChange={(event) => onSelectorChange(event.target.value)}
              sx={{
                minWidth: 240,
                '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.78rem' },
                '& .MuiInputLabel-root': { fontSize: '0.78rem' },
              }}
            >
              {tab.selectors.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <Chip size="small" label={selectedOption?.accessTarget ?? tab.accessTarget} variant="outlined" />
          {tab.sqlSource ? <Chip size="small" label={tab.sqlSource} variant="outlined" /> : null}
        </Stack>

        <FactoryBLToolbar
          actions={visibleActions}
          selectedRow={selectedRow}
          canEdit={canEdit}
          onAction={onAction}
        />
      </Stack>

      <DataTableMUI
        rows={rows}
        columns={columns as GridColDef[]}
        loading={loading}
        height="100%"
        hideFooter={rows.length <= 25}
        onRowClick={(params) => onSelectedRowChange(params.row as FactoryBLRow)}
      />
    </Stack>
  )
}

function MatchingWorkspace() {
  const [quality, setQuality] = useState('all')

  const stockRows: FactoryBLRow[] = [
    { id: 'stock-1', stack_id: 'BL-STK-2405', quality_code: 'R1', unallocated_bag: 180, unallocated_nw: 10800 },
    { id: 'stock-2', stack_id: 'BL-STK-2404', quality_code: 'R2', unallocated_bag: 120, unallocated_nw: 7200 },
  ]
  const salesRows: FactoryBLRow[] = [
    { id: 'sales-1', contract_no: 'S-2604-019', quality_code: 'R1', balance_bag: 120, balance_nw: 7200 },
    { id: 'sales-2', contract_no: 'LS-2604-011', quality_code: 'R2', balance_bag: 90, balance_nw: 5400 },
  ]

  const stockColumns = buildGridColumns([
    { field: 'stack_id', headerName: 'Stack', flex: 1, width: 130 },
    { field: 'quality_code', headerName: 'Quality', width: 100 },
    { field: 'unallocated_bag', headerName: 'Bag', width: 100, align: 'right', type: 'number' },
    { field: 'unallocated_nw', headerName: 'Net Wt', width: 130, align: 'right', type: 'number' },
  ])
  const salesColumns = buildGridColumns([
    { field: 'contract_no', headerName: 'Contract', flex: 1, width: 150 },
    { field: 'quality_code', headerName: 'Quality', width: 100 },
    { field: 'balance_bag', headerName: 'Bag', width: 100, align: 'right', type: 'number' },
    { field: 'balance_nw', headerName: 'Net Wt', width: 130, align: 'right', type: 'number' },
  ])

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
        <TextField
          select
          size="small"
          label="Quality"
          value={quality}
          onChange={(event) => setQuality(event.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="R1">R1</MenuItem>
          <MenuItem value="R2">R2</MenuItem>
        </TextField>
        <Chip size="small" label="factory_name=baoloc" variant="outlined" />
        <Chip size="small" label="frmMatchingPSMatch_WH_Stock_Sales" variant="outlined" />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 1.5,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            Sales Side
          </Typography>
          <DataTableMUI rows={salesRows} columns={salesColumns as GridColDef[]} height={360} hideFooter />
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            Stack Inventory
          </Typography>
          <DataTableMUI rows={stockRows} columns={stockColumns as GridColDef[]} height={360} hideFooter />
        </Paper>
      </Box>
    </Stack>
  )
}

function ActionDialog({
  pendingAction,
  onClose,
}: {
  pendingAction: PendingAction | null
  onClose: () => void
}) {
  if (!pendingAction || pendingAction.action === 'delete') return null

  const { action, row, tab } = pendingAction
  const entries = Object.entries(row ?? {}).filter(([field]) => field !== 'id')

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <span>{getActionLabel(action)} - {tab.label}</span>
          <Chip size="small" label={tab.accessTarget} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          {entries.length ? entries.map(([field, value]) => (
            <TextField
              key={field}
              label={field}
              value={value ?? ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: action !== 'new' }}
            />
          )) : tab.columns.slice(0, 8).map((column) => (
            <TextField key={column.field} label={column.headerName} value="" size="small" fullWidth />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onClose}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default function FactoryBLPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const snack = useSnackbar()
  const permission = usePermission('factoryBl')

  const [filters, setFilters] = useState<FactoryBaoLocDateRange>(readStoredDateRange)
  const [selectorValues, setSelectorValues] = useState<Record<string, string>>({})
  const [selectedRow, setSelectedRow] = useState<FactoryBLRow | null>(null)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [exporting, setExporting] = useState(false)

  const activeKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[2]
    return isFactoryBLTabKey(candidate) ? candidate : DEFAULT_FACTORY_BL_TAB
  }, [location.pathname])

  const activeTab = FACTORY_BL_TABS.find((tab) => tab.key === activeKey) ?? FACTORY_BL_TABS[0]
  const selectorValue = activeTab.selectors
    ? selectorValues[activeTab.key] ?? activeTab.selectors[0]?.value
    : undefined
  const invalidRange = filters.startDate > filters.endDate

  const { data: contextData } = useGetFactoryBaoLocContextQuery()
  const [saveDateRange] = useSaveFactoryBaoLocDateRangeMutation()
  const listQuery = useGetFactoryBaoLocTabRowsQuery(
    {
      tab: activeTab.endpoint,
      startDate: filters.startDate,
      endDate: filters.endDate,
      dataset: selectorValue,
    },
    { skip: activeTab.kind === 'summary' || activeTab.kind === 'matching' || invalidRange },
  )

  useEffect(() => {
    if (!location.pathname.startsWith('/factory/bao-loc')) return

    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[2]
    if (!isFactoryBLTabKey(candidate)) {
      navigate('/factory/bao-loc/summary', { replace: true })
    }
  }, [location.pathname, navigate])

  useEffect(() => {
    setSelectedRow(null)
  }, [activeKey, selectorValue])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters))
    }

    if (!invalidRange) {
      void saveDateRange(filters).unwrap().catch(() => undefined)
    }
  }, [filters, invalidRange, saveDateRange])

  const rows = useMemo(
    () => normalizeRows(listQuery.data?.items, activeTab.sampleRows),
    [activeTab.sampleRows, listQuery.data?.items],
  )

  const context = contextData ?? {
    title: 'BAO LOC FACTORY',
    storeChar: 'TLBL',
    factoryCode: 'baoloc',
    viewPrefix: 'tlc_factory_bl_',
    dateRange: filters,
    permissions: {
      canView: permission.canView,
      canEdit: permission.canEdit,
      canExport: permission.canView,
    },
  }

  const canEdit = permission.canEdit && context.permissions.canEdit

  const handleExport = async (filtered: boolean) => {
    setExporting(true)
    try {
      await downloadFactoryBaoLocExport({
        filtered,
        startDate: filters.startDate,
        endDate: filters.endDate,
        rows,
      })
      snack.success('Export started')
    } finally {
      setExporting(false)
    }
  }

  const handleAction = (action: FactoryBLAction) => {
    if (action !== 'new' && !selectedRow) return
    setPendingAction({ action, row: action === 'new' ? null : selectedRow, tab: activeTab })
  }

  if (!permission.canView) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={800}>
          No access
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <FactoryBLHeader
        title={context.title}
        filters={filters}
        invalidRange={invalidRange}
        exporting={exporting}
        onFilterChange={setFilters}
        onExport={(filtered) => void handleExport(filtered)}
      />

      {invalidRange ? (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      ) : null}

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeKey}
          onChange={(_, value: string) => {
            navigate(`/factory/bao-loc/${value}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 38,
            '& .MuiTabs-indicator': { height: 2 },
            '& .MuiTab-root': {
              minHeight: 38,
              py: 0.25,
              px: 1.35,
              fontSize: '0.78rem',
              fontWeight: 700,
              textTransform: 'none',
            },
          }}
        >
          {FACTORY_BL_TABS.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>

        <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.25, pt: 1.25 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight={900}>
              {activeTab.label}
            </Typography>
            <Chip size="small" label={context.factoryCode} />
            <Chip size="small" label={context.viewPrefix} variant="outlined" />
            {listQuery.data?.source ? <Chip size="small" label={listQuery.data.source} variant="outlined" /> : null}
          </Stack>
          <Divider />

          {activeTab.kind === 'summary' ? (
            <FactoryBLSummary />
          ) : activeTab.kind === 'matching' ? (
            <MatchingWorkspace />
          ) : (
            <FactoryBLListWorkspace
              tab={activeTab}
              rows={rows}
              loading={listQuery.isFetching}
              selectedRow={selectedRow}
              selectorValue={selectorValue}
              canEdit={canEdit}
              onSelectorChange={(value) => setSelectorValues((prev) => ({ ...prev, [activeTab.key]: value }))}
              onSelectedRowChange={setSelectedRow}
              onAction={handleAction}
            />
          )}
        </CardContent>
      </Card>

      <ActionDialog
        pendingAction={pendingAction}
        onClose={() => {
          setPendingAction(null)
        }}
      />
      <ConfirmDialog
        open={pendingAction?.action === 'delete'}
        title={`Delete ${activeTab.label}`}
        message={
          pendingAction?.row
            ? `Delete selected row ${pendingAction.row.id}?`
            : 'Delete selected row?'
        }
        destructive
        confirmText="Delete"
        onConfirm={() => {
          snack.info('Delete workflow is mapped for the selected row')
          setPendingAction(null)
        }}
        onCancel={() => setPendingAction(null)}
      />
    </Box>
  )
}
